/**
 * Unified Adventure Form Component
 * 
 * This component ensures 100% identical UI between the admin live preview
 * and the public custom adventure form. Both contexts use this same component
 * to guarantee consistent user experience.
 * 
 * @author Wildlife Project Team
 * @version 1.0.0
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MinusCircle, Plus, TrendingUp } from 'lucide-react';
import { CURRENCY_OPTIONS } from '@/components/business/features/custom-adventures/components/adventure-builder/constants';

interface UnifiedAdventureFormProps {
  options: any[];
  currency: string;
  formValues: Record<string, any>;
  onInputChange: (optionLabel: string, value: any, optionType: string) => void;
  onNumberChange: (optionLabel: string, delta: number) => void;
  getFormValue: (optionLabel: string) => any;
  className?: string;
}

export function UnifiedAdventureForm({
  options,
  currency,
  formValues,
  onInputChange,
  onNumberChange,
  getFormValue,
  className = ""
}: UnifiedAdventureFormProps) {
  const currencySymbol = CURRENCY_OPTIONS.find(c => c.value === currency)?.symbol || '$';

  return (
    <div className={`w-full space-y-4 ${className}`}>
      {options.map((opt, idx) => (
        <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start mb-3">
            <Label className="block text-gray-700 font-medium">
              {opt.label}
              {opt.required && <span className="text-red-500 ml-1">*</span>}
              {opt.isMultiplier && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  Multiplier
                </span>
              )}
            </Label>
            
            {/* Show pricing info if available */}
            {opt.pricing && opt.pricing.length > 0 && (
              <div className="text-right">
                {opt.pricing.map((price: any, priceIdx: number) => (
                  <div key={priceIdx} className="text-sm text-gray-500">
                    {price.type === 'flat' && `+${currencySymbol}${price.value}`}
                    {price.type === 'fixed' && `+${currencySymbol}${price.value}`}
                    {price.type === 'percentage' && `+${price.value}%`}
                    {price.type === 'per_unit' && `+${currencySymbol}${price.value} per unit`}
                    {price.type === 'per_person' && `+${currencySymbol}${price.value} per person`}
                    {price.type === 'per_day' && `+${currencySymbol}${price.value} per day`}
                    {price.type === 'per_person_day' && `+${currencySymbol}${price.value} per person/day`}
                    {price.description && <span className="text-xs italic ml-1">({price.description})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Interactive form elements */}
          {opt.type === 'text' && (
            <Input 
              type="text"
              className="w-full transition-all focus:ring-primary focus:border-primary"
              placeholder={`Enter ${opt.label.toLowerCase()}`}
              value={getFormValue(opt.label) || ''}
              onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
            />
          )}
          
          {opt.type === 'textarea' && (
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
              placeholder={`Enter ${opt.label.toLowerCase()}...`}
              value={getFormValue(opt.label) || ''}
              onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
              rows={3}
            />
          )}
          
          {opt.type === 'email' && (
            <Input 
              type="email"
              className="w-full transition-all focus:ring-primary focus:border-primary"
              placeholder="Enter email address"
              value={getFormValue(opt.label) || ''}
              onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
            />
          )}
          
          {opt.type === 'phone' && (
            <Input 
              type="tel"
              className="w-full transition-all focus:ring-primary focus:border-primary"
              placeholder="Enter phone number"
              value={getFormValue(opt.label) || ''}
              onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
            />
          )}
          
          {opt.type === 'date' && (
            <Input 
              type="date"
              className="w-full transition-all focus:ring-primary focus:border-primary"
              value={getFormValue(opt.label) || ''}
              onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
            />
          )}
          
          {opt.type === 'time' && (
            <Input 
              type="time"
              className="w-full transition-all focus:ring-primary focus:border-primary"
              value={getFormValue(opt.label) || ''}
              onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
            />
          )}
          
          {opt.type === 'file' && (
            <Input 
              type="file"
              className="w-full transition-all focus:ring-primary focus:border-primary"
              onChange={(e) => onInputChange(opt.label, e.target.files?.[0] || null, opt.type)}
            />
          )}
          
          {opt.type === 'radio' && opt.options && (
            <div className="space-y-2">
              {opt.options.map((option: any, optIdx: number) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const pricingDisplay = typeof option === 'object' && option.pricing 
                  ? ` (+${option.pricing.map((p: any) => {
                      if (p.type === 'flat' || p.type === 'fixed') return `${currencySymbol}${p.value}`;
                      if (p.type === 'per_person') return `${currencySymbol}${p.value}/person`;
                      if (p.type === 'per_day') return `${currencySymbol}${p.value}/day`;
                      return `${currencySymbol}${p.value}`;
                    }).join(', ')})`
                  : '';
                
                return (
                  <div key={optIdx} className="flex items-center gap-2">
                    <input
                      type="radio"
                      id={`radio-${idx}-${optIdx}`}
                      name={`radio-${idx}`}
                      value={optionValue}
                      checked={getFormValue(opt.label) === optionValue}
                      onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
                      className="text-primary focus:ring-primary"
                    />
                    <Label 
                      htmlFor={`radio-${idx}-${optIdx}`}
                      className="text-sm text-gray-700 cursor-pointer select-none"
                    >
                      {optionValue}{pricingDisplay}
                    </Label>
                  </div>
                );
              })}
            </div>
          )}
          
          {opt.type === 'number' && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                className="h-8 w-8 p-0 rounded-full hover:bg-red-50 hover:border-red-300 transition-colors"
                onClick={() => onNumberChange(opt.label, -1)}
                disabled={(getFormValue(opt.label) || 0) <= (opt.required ? 1 : 0)}
              >
                <MinusCircle className="h-4 w-4" />
              </Button>
              
              <Input
                type="number"
                min={opt.required ? "1" : "0"}
                value={getFormValue(opt.label) || (opt.required ? 1 : 0)}
                onChange={(e) => onInputChange(opt.label, e.target.value, opt.type)}
                className="w-20 text-center focus:ring-primary focus:border-primary transition-all"
              />
              
              <Button
                variant="outline"
                size="sm"
                type="button"
                className="h-8 w-8 p-0 rounded-full hover:bg-green-50 hover:border-green-300 transition-colors"
                onClick={() => onNumberChange(opt.label, 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
              
              {opt.isMultiplier && opt.multiplierTarget && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 bg-blue-50 ml-2 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        Multiplier
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="bg-white text-gray-700 border shadow-lg max-w-sm">
                      <p>This affects pricing for "{opt.multiplierTarget}"</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Changes here will multiply the cost of {opt.multiplierTarget} options
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          
          {opt.type === 'checkbox' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`checkbox-${idx}`}
                checked={getFormValue(opt.label) || false}
                onCheckedChange={(checked) => onInputChange(opt.label, checked, opt.type)}
                className="text-primary border-gray-300 focus:ring-primary"
              />
              <Label 
                htmlFor={`checkbox-${idx}`}
                className="text-sm text-gray-700 cursor-pointer select-none"
              >
                {opt.label}
              </Label>
            </div>
          )}
          
          {(opt.type === 'select' || opt.type === 'dropdown') && opt.options && (
            <div className="space-y-2">
              <Select
                value={getFormValue(opt.label) || ''}
                onValueChange={(value) => onInputChange(opt.label, value, opt.type)}
              >
                <SelectTrigger className="w-full focus:ring-primary focus:border-primary transition-all">
                  <SelectValue placeholder={`Select ${opt.label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {opt.options.map((option: any, optIdx: number) => {
                    // Enhanced option display with pricing info
                    if (typeof option === 'string') {
                      return (
                        <SelectItem key={optIdx} value={option}>
                          {option}
                        </SelectItem>
                      );
                    } else if (typeof option === 'object' && option.value) {
                      // Show pricing in option text for better UX
                      const pricingDisplay = option.pricing && option.pricing.length > 0
                        ? ` (+${option.pricing.map((p: any) => {
                            if (p.type === 'flat' || p.type === 'fixed') return `${currencySymbol}${p.value}`;
                            if (p.type === 'per_person') return `${currencySymbol}${p.value}/person`;
                            if (p.type === 'per_day') return `${currencySymbol}${p.value}/day`;
                            if (p.type === 'per_person_day') return `${currencySymbol}${p.value}/person/day`;
                            if (p.type === 'percentage') return `${p.value}%`;
                            return `${currencySymbol}${p.value}`;
                          }).join(', ')})`
                        : '';
                      
                      return (
                        <SelectItem key={optIdx} value={option.value}>
                          {option.value}{pricingDisplay}
                        </SelectItem>
                      );
                    }
                    return null;
                  })}
                </SelectContent>
              </Select>
              
              {/* Enhanced: Show detailed selected choice pricing */}
              {getFormValue(opt.label) && Array.isArray(opt.options) && (
                <div className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const selectedChoice = opt.options.find((choice: any) =>
                      typeof choice === 'object' && choice.value === getFormValue(opt.label)
                    );
                    if (selectedChoice && typeof selectedChoice === 'object' && selectedChoice.pricing) {
                      return (
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-green-800">Selected: {selectedChoice.value}</span>
                            <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-300">
                              Choice Pricing
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            {selectedChoice.pricing.map((pricing: any, pIdx: number) => (
                              <div key={pIdx} className="text-green-700 font-medium">
                                {pricing.type === 'flat' || pricing.type === 'fixed' ? (
                                  `+${currencySymbol}${pricing.value} (fixed)`
                                ) : pricing.type === 'percentage' ? (
                                  `+${pricing.value}% of base price`
                                ) : pricing.type === 'per_person' ? (
                                  `+${currencySymbol}${pricing.value} per person`
                                ) : pricing.type === 'per_day' ? (
                                  `+${currencySymbol}${pricing.value} per day`
                                ) : pricing.type === 'per_person_day' ? (
                                  `+${currencySymbol}${pricing.value} per person per day`
                                ) : (
                                  `+${currencySymbol}${pricing.value}`
                                )}
                                {pricing.description && (
                                  <span className="italic text-green-600 text-xs ml-1">
                                    - {pricing.description}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
} 